import { useMutation } from '@tanstack/react-query';
import { queryClient } from 'Context/QueryClientContext';
import ObrasApi from 'Data/Obras.api';
import { IUpdateObra } from 'Data/interfaces/Obra/ICreateOrUpdate';
import { IObraResponse } from 'Data/interfaces/Obra/IObraListResponse';
import { Mixpanel } from 'Utils/MixPanel';
import { store } from 'Store';
import { Dispatch } from 'redux';
import toastHandler from 'Utils/toastHandler';
import { IUserLimitsActive } from 'Store/Auth/interfaces/IAuthState';
import { AuthActions } from 'Store/Auth/Auth.actions';
import { createError } from 'Utils/Messages';
import { OBRA_QUERY_KEY } from 'Data/keys/QueryKeys';

interface IUseQueryUpdateObraProps {
  dispatch: Dispatch;
}

export const useQueryUpdateObra = ({ dispatch }: IUseQueryUpdateObraProps) => {
  const { mutate, isPending } = useMutation({
    mutationFn: (variables: IUpdateObra) => {
      return ObrasApi.update(variables.values);
    },
    onMutate: async (variables: IUpdateObra) => {
      const previousObras = queryClient.getQueryData<IObraResponse[]>([OBRA_QUERY_KEY]);
      const previousObra = queryClient.getQueryData<IObraResponse>([
        OBRA_QUERY_KEY,
        Number(variables.values.ConstructionSiteId),
      ]);

      queryClient.setQueryData<IObraResponse[]>([OBRA_QUERY_KEY], (oldData) => {
        return oldData?.map((obra) => {
          if (obra.ConstructionSiteId === variables.values.ConstructionSiteId) {
            return { ...obra, ...variables.values };
          }
          return obra;
        });
      });
      queryClient.setQueryData(
        [OBRA_QUERY_KEY, Number(variables.values.ConstructionSiteId)],
        variables.values,
      );

      return { previousObras, previousObra };
    },
    onSuccess: async ({ data }, variables) => {
      const obra = data.data?.Value?.data;

      await Promise.all([
        queryClient.cancelQueries({ queryKey: [OBRA_QUERY_KEY] }),
        queryClient.cancelQueries({
          queryKey: [OBRA_QUERY_KEY, Number(variables.values.ConstructionSiteId)],
        }),
      ]);

      queryClient.setQueryData(
        [OBRA_QUERY_KEY, Number(variables.values.ConstructionSiteId)],
        obra,
      );

      const UsersWithConstruction = store.getState().tenant?.UsersWithConstruction;
      const isFirstUser =
        (UsersWithConstruction?.tenantInvitedUsers?.length || 0) <= 0 &&
        (UsersWithConstruction?.tenantUsers?.length || 0) <= 0 &&
        obra?.InvitedUsers?.length > 0;
      if (isFirstUser) {
        Mixpanel.track({
          name: 'INVITED_FIRST_USER',
          props: { constructionSiteId: obra.ConstructionSiteId },
          userInfo: variables.userInfoTracking,
          currentListTenant: variables.currentTenantTracking,
        });
      }

      const userLimitsActive: IUserLimitsActive = {
        QtyActiveUsers:
          obra?.InvitedUsers?.filter((newUsers: any) => {
            return !UsersWithConstruction?.tenantInvitedUsers
              ?.map((user: any) => {
                return user.InvitedUserEmail;
              })
              .includes(newUsers.Email);
          }).length || undefined,
      };
      dispatch(AuthActions.toogleUserLimitsActive(userLimitsActive));

      toastHandler.showSuccess('Obra Atualizada com sucesso!');
    },
    onError: async (error: any, variables, context) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: [OBRA_QUERY_KEY] }),
        queryClient.cancelQueries({
          queryKey: [OBRA_QUERY_KEY, Number(variables.values.ConstructionSiteId)],
        }),
      ]);

      queryClient.setQueryData([OBRA_QUERY_KEY], context?.previousObras);
      queryClient.setQueryData(
        [OBRA_QUERY_KEY, Number(variables.values.ConstructionSiteId)],
        context?.previousObra,
      );

      const errorMsg = error.errors ? error.errors[0].Message : createError('obra');
      toastHandler.showError(errorMsg);
    },
  });

  return {
    updateObra: mutate,
    isLoading: isPending,
  };
};
